import { DefaultTFuncReturn, t } from "i18next";
import { toast } from "~/components/toast";
import { IToastOptions, ToastId, ToastType } from "~/components/toast/types";
import { triggerShowLimitsModal } from "~/helpers/customEvents";
import { Limits } from "~/types/billing";

export type ToastMessage = {
  loading?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions };
  success?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions };
  error?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions };
};

type ResponseWithError<T = any> =
  | { response: null; errors: string[] }
  | { response: T; errors: null };

enum ErrorCode {
  REQUIRES_UPGRADE = 1,
}

const fromToastMessage = (
  type: ToastType,
  message?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions },
  toastId?: ToastId
) => {
  if (!message) {
    if (toastId) {
      toast.dismiss(toastId);
    }

    return;
  }

  if (typeof message === "string") {
    return toast.updateOrCreate(toastId, {
      type,
      render: message,
    });
  }

  return toast.updateOrCreate(toastId, {
    type,
    render: message.body,
    ...message.options,
  });
};

const handleErrors = async <T = any>(
  promise: () => Promise<T>,
  toasts: ToastMessage
): Promise<ResponseWithError<T>> => {
  const toastId = fromToastMessage("info", toasts.loading);

  try {
    const response = await promise();
    if (toasts.success) fromToastMessage("success", toasts.success, toastId);
    return { response, errors: null };
  } catch (error: any) {
    // errorData can be null/empty or not in DRF's { field: [msg] } shape
    // (e.g. a proxy/CDN error), so guard it instead of throwing here.
    const handleFormErrors = (errorData: unknown) => {
      if (!errorData || typeof errorData !== "object") return [];
      return Object.values(errorData as Record<string, unknown>)
        .map((value) => (Array.isArray(value) ? value[0] : value))
        .filter((value): value is string => typeof value === "string");
    };

    let errors: string[] | null = [];
    const errorData = error.response?.status;

    if (errorData === 429) {
      const retryAfterRaw = error.response?.headers?.["retry-after"];
      const retryAfter = Number(retryAfterRaw);
      const message =
        Number.isFinite(retryAfter) && retryAfter > 0
          ? t("errors.too_many_attempts_retry", {
              seconds: Math.ceil(retryAfter),
            })
          : t("errors.too_many_attempts");

      fromToastMessage("error", message, toastId);

      return { response: null, errors: [message as string] };
    }

    if (errorData === 400 || errorData === 402 || errorData === 404) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.detail;

      if (error.response?.data?.error_code === ErrorCode.REQUIRES_UPGRADE) {
        triggerShowLimitsModal(Limits.PREMIUM_KEYWORDS);
      }

      if (message) {
        fromToastMessage("error", message, toastId);

        return { response: null, errors: [message] };
      }

      if (/text\/html/.test(error?.response?.headers?.["content-type"])) {
        fromToastMessage("error", toasts.error, toastId);
      } else {
        errors = handleFormErrors(error?.response?.data);

        if (errors.length) {
          fromToastMessage("error", errors[0], toastId);
        } else {
          fromToastMessage("error", toasts.error, toastId);
        }
      }
    } else {
      fromToastMessage("error", toasts.error, toastId);
    }

    return {
      response: null,
      errors: errors.length ? errors : [error.toString()],
    };
  }
};

export default handleErrors;
